import { Trans } from "@lingui/react/macro";
import React from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import "dayjs/locale/sl";
import { DatePickerInput, DateValue, MonthPickerInput } from "@mantine/dates";
import _ from "lodash";
import { Button } from "@heroui/react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const DatePicker = ({
  selectedDate,
  calendarView,
  onDateChange,
  showTodayButton = true,
  minDate,
  maxDate,
  overrideStartDate,
  overrideEndDate,
}: {
  selectedDate: dayjs.Dayjs;
  calendarView: "day" | "week" | "month";
  onDateChange: (date: dayjs.Dayjs) => void;
  showTodayButton?: boolean;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  overrideStartDate?: dayjs.Dayjs;
  overrideEndDate?: dayjs.Dayjs;
}) => {
  const [hovered, setHovered] = React.useState<Date | null>(null);

  const canGoForward =
    !maxDate ||
    selectedDate.startOf(calendarView).add(1, calendarView).isBefore(maxDate);
  const canGoBack =
    !minDate ||
    selectedDate.endOf(calendarView).subtract(1, calendarView).isAfter(minDate);

  const handleNextClick = () => {
    const newDate = selectedDate.add(1, calendarView);

    if (!canGoForward) return;

    onDateChange(newDate);
  };

  const handlePrevClick = () => {
    const newDate = selectedDate.subtract(1, calendarView);

    if (!canGoBack) return;

    onDateChange(newDate);
  };

  const handleDaySelect = (date: DateValue) => {
    if (_.isArray(date)) {
      onDateChange(dayjs(date[0]));
      return;
    }

    onDateChange(dayjs(date));
  };

  let value: Date | Date[] = new Date();
  if (calendarView === "day") {
    value = selectedDate.toDate();
  } else if (calendarView === "week") {
    const start = selectedDate.locale("sl").weekday(0);
    const end = selectedDate.locale("sl").weekday(6);
    value = [start.toDate(), end.toDate()];
  } else if (calendarView === "month") {
    const start = selectedDate.locale("sl").startOf("month");
    value = [start.toDate(), selectedDate.locale("sl").endOf("month").toDate()];
  }

  if (_.isArray(value)) {
    if (overrideStartDate) {
      value[0] = overrideStartDate.toDate();
    }

    if (overrideEndDate) {
      value[1] = overrideEndDate.toDate();
    }
  } else {
    if (overrideStartDate) {
      value = overrideStartDate.toDate();
    }

    if (overrideEndDate) {
      value = overrideEndDate.toDate();
    }
  }

  return (
    <Wrapper>
      {showTodayButton && (
        <Button
          onPress={() => onDateChange(dayjs())}
          variant="flat"
          startContent={<Calendar size={16} />}
        >
          <p className="text-sm font-medium">
            <Trans>Danes</Trans>
          </p>
        </Button>
      )}
      <div className="flex items-center gap-0">
        <Button
          onPress={handlePrevClick}
          data-identifier="prev-day-button"
          isDisabled={!canGoBack}
          variant="light"
          isIconOnly
          size="sm"
        >
          <ChevronLeft size={16} />
        </Button>
        <Button variant="light" size="sm" data-identifier="date-picker-outer">
          {calendarView === "month" ? (
            <MonthPickerInput
              // locale={navigator.language}
              level={"year"}
              defaultLevel={"year"}
              type={"default"}
              value={selectedDate.toDate()}
              onChange={handleDaySelect}
              variant={"unstyled"}
              valueFormat={"MMMM YYYY"}
              closeOnChange={true}
              minDate={minDate?.toDate()}
              maxDate={maxDate?.toDate()}
              withCellSpacing={false}
              getMonthControlProps={(date) => {
                const isSelected = selectedDate.isSame(date, calendarView);
                const isHovered =
                  hovered && dayjs(hovered).isSame(date, calendarView);

                const dateDayObject = dayjs(date);

                const isBeforeMinDate =
                  minDate && dateDayObject.isBefore(minDate, "day");
                const isAfterMaxDate =
                  maxDate && dateDayObject.isAfter(maxDate, "day");

                const isTodayDate = dateDayObject.isSame(dayjs(), "month");
                const shouldHighlightTodayDate = isTodayDate && !isSelected;

                return {
                  selected: isSelected,
                  onMouseEnter: () => setHovered(date),
                  onMouseLeave: () => setHovered(null),
                  inRange: isHovered || isSelected,
                  onClick: () => {
                    handleDaySelect(date);
                  },
                  disabled: isBeforeMinDate || isAfterMaxDate,
                  bg: shouldHighlightTodayDate ? "#d20d0d" : undefined,
                  c: shouldHighlightTodayDate ? "white" : undefined,
                };
              }}
            />
          ) : (
            <DatePickerInput
              // locale={navigator.language}
              data-identifier="date-picker-input"
              level={"month"}
              defaultLevel={"month"}
              type={calendarView === "day" ? "default" : "range"}
              value={value as DateValue}
              onChange={(value) => {
                handleDaySelect(value as DateValue);
              }}
              variant={"unstyled"}
              valueFormat={"DD. MM. YY"}
              closeOnChange={true}
              minDate={minDate?.toDate()}
              maxDate={maxDate?.toDate()}
              withCellSpacing={false}
              getDayProps={(date) => {
                const isSelected = selectedDate
                  .locale("sl")
                  .isSame(date, calendarView);
                const isHovered =
                  hovered &&
                  dayjs(hovered).locale("sl").isSame(date, calendarView);

                const dateDayObject = dayjs(date).locale("sl");

                const isBeforeMinDate =
                  minDate && dateDayObject.isBefore(minDate, "day");
                const isAfterMaxDate =
                  maxDate && dateDayObject.isAfter(maxDate, "day");

                const isTodayDate = dateDayObject.isSame(dayjs(), "day");
                const shouldHighlightTodayDate = isTodayDate && !isSelected;

                return {
                  selected: isSelected,
                  onMouseEnter: () => setHovered(date),
                  onMouseLeave: () => setHovered(null),
                  inRange: isHovered || isSelected,
                  onClick: () => {
                    handleDaySelect(date);
                  },
                  disabled: isBeforeMinDate || isAfterMaxDate,
                  bg: shouldHighlightTodayDate ? "red" : undefined,
                  c: shouldHighlightTodayDate ? "white" : undefined,
                  style: shouldHighlightTodayDate
                    ? { borderRadius: "50%" }
                    : undefined,
                };
              }}
            />
          )}
        </Button>

        <Button
          onPress={handleNextClick}
          data-identifier="next-day-button"
          isDisabled={!canGoForward}
          variant="light"
          isIconOnly
          size="sm"
        >
          <ChevronRight size={16} />
        </Button>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  margin: auto;
  user-select: none;
  color: white !important;

  display: flex;
  align-items: center;

  justify-content: space-around;

  flex: 1;

  flex-wrap: wrap;

  @media (min-width: 400px) {
    gap: 10px;

    flex-wrap: unset;
  }

  @media (min-width: 768px) {
    margin: 0;
    gap: 15px;
  }

  @media (min-width: 768px) {
    justify-content: unset;
  }
`;

// const Outer = styled.div`
//   user-select: none;
//   display: flex;
//   position: relative;
//   align-items: center;
//   justify-content: space-around;
//   border-radius: 15px;
//   color: #6c7b86;
//   padding: 7px 2px;
//   transition: all 0.25s cubic-bezier(0.47, 0, 0.75, 1);
//   width: auto;

//   .mantine-DatePickerInput-input {
//     white-space: pre;
//   }

//   button {
//     border: none;
//     background: none;
//     color: #6c7b86;
//     font-weight: 600;
//   }

//   > .icon {
//     font-size: 2rem;
//     cursor: pointer;
//   }

//   label {
//     font-size: 1rem;
//     color: #6c7b86;
//     font-weight: 500;
//   }

//   @media (min-width: 400px) {
//     padding: 7px 10px;
//   }

//   @media (min-width: 768px) {
//     padding: 4px 5px;
//   }
// `;

export default React.memo(DatePicker);
